import ModalController from "../../utils/ModalController";
import { initSectionThemeTriggers } from "../../utils/sectionThemeTriggers";
import { mountTurnstile, unmountTurnstile, waitForTurnstile, resetTurnstile } from "../../utils/turnstile";
import { flushQueuedEvents } from "../../utils/data-layer";

export default class AuthModal {
  constructor(el) {
    this.el = el;
    initSectionThemeTriggers(el);
    this.backdrop = el.querySelector(".c-auth-modal__backdrop");
    this.closeBtn = el.querySelector(".c-auth-modal__close");
    this.views = {
      signin: el.querySelector(".c-auth-modal__signin"),
      signup: el.querySelector(".c-auth-modal__signup"),
      forgot: el.querySelector(".c-auth-modal__forgot-view"),
    };
    this.loginForm = el.querySelector('[data-auth-form="login"]');
    this.registerForm = el.querySelector('[data-auth-form="register"]');
    this.forgotForm = el.querySelector('[data-auth-form="forgot"]');

    this.ajaxUrl = el.dataset.ajaxUrl;
    this.nonces = {
      login: el.dataset.nonceLogin,
      register: el.dataset.nonceRegister,
      forgot: el.dataset.nonceForgot,
    };
    this.actions = {
      login: "vs_ajax_login",
      register: "vs_ajax_register",
      forgot: "vs_ajax_forgot_password",
    };

    document.body.appendChild(this.el);

    this.controller = new ModalController({
      modal: this.el,
      backdrop: this.backdrop,
      closeBtn: this.closeBtn,
      // Only the visible view's widget runs, and none while closed.
      onOpen: () => mountTurnstile(this.activeView()),
      onClose: () => {
        this.removeLoginParam();
        unmountTurnstile(this.el);
      },
    });

    this.init();
  }

  init() {
    document.querySelectorAll("[data-auth-trigger]").forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.controller.open();
      });
    });

    document.addEventListener("click", (e) => {
      const trigger = e.target.closest(".wc-block-checkout__login-prompt");
      if (!trigger) return;
      e.preventDefault();
      this.controller.open();
    });

    if (!window.vsAssets?.isLoggedIn && new URLSearchParams(window.location.search).get("login") === "1") {
      this.controller.open();
    }

    this.el.querySelectorAll("[data-auth-switch]").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.switchView(btn.dataset.authSwitch);
      });
    });

    this.loginForm.addEventListener("submit", (e) => this.handleSubmit(e, "login"));
    this.registerForm.addEventListener("submit", (e) => this.handleSubmit(e, "register"));
    this.forgotForm.addEventListener("submit", (e) => this.handleSubmit(e, "forgot"));
  }

  removeLoginParam() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("login")) return;
    url.searchParams.delete("login");
    window.history.replaceState({}, "", url);
  }

  switchView(view) {
    this.clearErrors();
    const target = this.views[view] ? view : "signin";
    Object.entries(this.views).forEach(([name, el]) => {
      el.classList.toggle("is-active", name === target);
      if (name !== target) unmountTurnstile(el);
    });
    if (this.controller.isOpen) mountTurnstile(this.views[target]);
  }

  activeView() {
    return Object.values(this.views).find((el) => el.classList.contains("is-active")) || this.views.signin;
  }

  clearErrors() {
    this.el.querySelectorAll(".c-auth-modal__error").forEach((el) => {
      el.textContent = "";
      el.classList.remove("is-visible");
    });
  }

  showError(form, message) {
    const errorEl = form.querySelector(".c-auth-modal__error");
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add("is-visible");
    }
  }

  async handleSubmit(e, type) {
    e.preventDefault();
    const form = e.currentTarget;
    const submitBtn = form.querySelector(".c-cta");

    this.removeLoginParam();

    if (submitBtn) submitBtn.classList.add("is-loading");
    this.clearErrors();

    // The widget also writes the token into a hidden input of the form, picked up below.
    await waitForTurnstile(form);

    const formData = new FormData(form);
    formData.append("action", this.actions[type]);
    formData.append("security", this.nonces[type]);

    const ajaxUrl = this.ajaxUrl || window.vsAssets?.ajaxUrl;

    fetch(ajaxUrl, {
      method: "POST",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (submitBtn) submitBtn.classList.remove("is-loading");
        resetTurnstile(form);

        if (data.success) {
          if (type === "login") {
            window.location.reload();
          } else if (type === "register") {
            // New account: sign_up (method auth_modal).
            flushQueuedEvents();
            form.reset();
            this.switchView("signin");
            this.showSuccess(this.loginForm, data.data.message);
          } else {
            this.showSuccess(form, data.data.message);
          }
        } else {
          this.showError(form, data.data?.message || "Something went wrong.");
        }
      })
      .catch(() => {
        if (submitBtn) submitBtn.classList.remove("is-loading");
        resetTurnstile(form);
        this.showError(form, "An error occurred. Please try again.");
      });
  }

  showSuccess(form, message) {
    const errorEl = form.querySelector(".c-auth-modal__error");
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add("is-visible", "is-success");
    }
  }
}
